import { Capacitor, CapacitorHttp } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { supabase } from "@/integrations/supabase/client";
import { logInfo } from "@/services/eventLogService";

export type AppPlatform = "ios" | "android";

export type UpdateStatus = "ok" | "soft_update" | "force_update";

export interface AppVersionRow {
  platform: AppPlatform;
  latest_version: string;
  min_supported_version: string;
  release_notes: string | null;
  store_url: string;
  banner_enabled: boolean;
  blocking_enabled: boolean;
  updated_at: string;
}

export interface AppVersionCheckResult {
  status: UpdateStatus;
  currentVersion: string | null;
  latestVersion: string | null;
  releaseNotes: string | null;
  storeUrl: string | null;
}

export const DEFAULT_RESULT: AppVersionCheckResult = {
  status: "ok",
  currentVersion: null,
  latestVersion: null,
  releaseNotes: null,
  storeUrl: null,
};

/**
 * compareVersions("1.2.3", "1.2.4") === -1
 * compareVersions("1.2.3", "1.2.3") === 0
 * compareVersions("1.3.0", "1.2.9") === 1
 *
 * Lenient: pads missing segments with 0, ignores leading "v",
 * treats anything non-numeric as 0 to avoid false-positive blocking
 * if a malformed value lands in the app_versions table.
 */
export const compareVersions = (a: string, b: string): number => {
  const parse = (v: string): number[] =>
    v
      .replace(/^v/i, "")
      .split(/[.\-+]/)
      .map((part) => {
        const n = parseInt(part, 10);
        return Number.isFinite(n) ? n : 0;
      });

  const aParts = parse(a);
  const bParts = parse(b);
  const len = Math.max(aParts.length, bParts.length);

  for (let i = 0; i < len; i++) {
    const av = aParts[i] ?? 0;
    const bv = bParts[i] ?? 0;
    if (av < bv) return -1;
    if (av > bv) return 1;
  }
  return 0;
};

export const extractAppStoreId = (storeUrl: string): string | null => {
  const m = storeUrl.match(/\/id(\d+)/);
  return m ? m[1] : null;
};

export const extractPlayStorePackageName = (
  storeUrl: string,
): string | null => {
  try {
    const u = new URL(storeUrl);
    const id = u.searchParams.get("id");
    return id && id.length > 0 ? id : null;
  } catch {
    return null;
  }
};

/**
 * Play Store has no public version-lookup API. The version is embedded in
 * a Google-internal JSON-ish blob inside the HTML page, in the form
 * `[[["X.Y.Z"`. This is brittle by definition — Google could change the
 * format any time — so the caller must fail open if this returns null.
 */
export const parsePlayStoreVersion = (html: string): string | null => {
  const m = html.match(/\[\[\["(\d+(?:\.\d+){1,3})"/);
  return m ? m[1] : null;
};

/**
 * Looks up the version currently live in the App Store via Apple's public
 * iTunes lookup endpoint. Returns null on any failure — caller must fail
 * open (no banner gating).
 */
export const fetchAppStoreVersion = async (
  appStoreId: string,
): Promise<string | null> => {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const r = await fetch(
      `https://itunes.apple.com/lookup?id=${appStoreId}&country=il`,
      { signal: controller.signal },
    );
    clearTimeout(timeout);
    if (!r.ok) return null;
    const j = await r.json();
    const v = j?.results?.[0]?.version;
    return typeof v === "string" && v.length > 0 ? v : null;
  } catch {
    return null;
  }
};

/**
 * Looks up the version currently live in the Play Store by scraping the
 * public details page. Uses CapacitorHttp so it can bypass the WebView's
 * CORS restrictions on native; on web (where this never runs from the
 * gate anyway) it would fail with a CORS error and return null.
 *
 * Returns null on any failure — caller must fail open.
 */
export const fetchPlayStoreVersion = async (
  packageName: string,
): Promise<string | null> => {
  try {
    const res = await CapacitorHttp.get({
      url: `https://play.google.com/store/apps/details?id=${encodeURIComponent(packageName)}&hl=en`,
      headers: {
        "User-Agent":
          "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0 Safari/537.36",
      },
      readTimeout: 3000,
      connectTimeout: 3000,
    });
    if (res.status !== 200) return null;
    const html = typeof res.data === "string" ? res.data : String(res.data);
    return parsePlayStoreVersion(html);
  } catch {
    return null;
  }
};

/**
 * Cache the store-side version lookup to absorb `appStateChange` →
 * foreground churn (otherwise every foreground re-hits the store; on
 * Android that's ~1 MB of Play Store HTML each time).
 *
 * Kept SHORT and per-platform on purpose: the banner must appear on
 * essentially every open of an outdated app, so a stale "no update yet"
 * reading can only survive for the TTL after a release goes live — not
 * the 6 h it used to, which left users banner-less for hours after the
 * store had the build. iOS uses Apple's tiny iTunes JSON lookup so it can
 * refresh aggressively; Android scrapes a ~1 MB page so it gets a longer
 * (but still short) window. Only users already flagged as behind ever hit
 * this path (see the store-check gate in checkAppVersion), so up-to-date
 * users never pay the network cost regardless of TTL.
 */
const STORE_VERSION_CACHE_MS: Record<AppPlatform, number> = {
  ios: 5 * 60 * 1000,
  android: 30 * 60 * 1000,
};
interface CacheEntry {
  version: string | null;
  at: number;
}
const storeVersionCache = new Map<string, CacheEntry>();

/** Test-only escape hatch — cleared between tests so cached nulls don't bleed. */
export const __resetStoreVersionCache = (): void => {
  storeVersionCache.clear();
};

const fetchStoreVersionForRow = async (
  platform: AppPlatform,
  row: AppVersionRow,
): Promise<string | null> => {
  if (platform === "ios") {
    const id = extractAppStoreId(row.store_url);
    return id ? fetchAppStoreVersion(id) : null;
  }
  const pkg = extractPlayStorePackageName(row.store_url);
  return pkg ? fetchPlayStoreVersion(pkg) : null;
};

const getCachedStoreVersion = async (
  platform: AppPlatform,
  row: AppVersionRow,
): Promise<string | null> => {
  const key = `${platform}:${row.store_url}`;
  const now = Date.now();
  const hit = storeVersionCache.get(key);
  if (hit && now - hit.at < STORE_VERSION_CACHE_MS[platform]) {
    return hit.version;
  }
  const version = await fetchStoreVersionForRow(platform, row);
  storeVersionCache.set(key, { version, at: now });
  return version;
};

const getPlatform = (): AppPlatform | null => {
  if (!Capacitor.isNativePlatform()) return null;
  const p = Capacitor.getPlatform();
  if (p === "ios" || p === "android") return p;
  return null;
};

const getCurrentVersion = async (): Promise<string | null> => {
  try {
    const info = await CapApp.getInfo();
    return info.version || null;
  } catch {
    return null;
  }
};

/**
 * Fire-and-forget: tell the database that this device is running `version`
 * on `platform`. The server decides whether to believe it: the caller must be
 * signed in, an admin's report raises `latest_version` on its own, and anyone
 * else's is one vote out of the three it takes (migration 20261012100000).
 * Signed out, the call is refused — harmless, it is retried on the next check.
 */
const reportVersion = async (
  platform: AppPlatform,
  version: string,
): Promise<void> => {
  try {
    await supabase.rpc("report_app_version", {
      p_platform: platform,
      p_version: version,
    });
  } catch {
    // Best-effort — never block the app for this.
  }
};

/**
 * Pure helper — given the DB-declared latest/min and what's actually on
 * the store (or null if we couldn't find out), return the effective
 * values to compare the device version against.
 *
 * For the **soft banner**, the live store version is the source of truth:
 * it's exactly what the user can install right now. Using it directly
 * (rather than as a mere ceiling on the DB value) fixes two failures:
 *   1. The banner fires the moment a new build goes live, even if nobody
 *      bumped `latest_version` in the database.
 *   2. It never fires for a version that isn't on the store yet — the
 *      previous "ceiling-only" logic suppressed the banner entirely
 *      whenever the DB knew about a release before the store served it
 *      (e.g. a self-reported TestFlight build), which is the bug that
 *      hid the 1.2.2 update prompt.
 * The DB `latest_version` is only a fallback for when the store lookup
 * fails (fail open).
 *
 * For the **blocking force-update** path the store stays a *ceiling* — we
 * never block a user below a `min_supported_version` that has no
 * installable build on the store.
 */
export const computeEffectiveVersions = (
  declaredLatest: string,
  declaredMin: string,
  storeVersion: string | null,
): { effectiveLatest: string; effectiveMin: string } => {
  if (!storeVersion) {
    return { effectiveLatest: declaredLatest, effectiveMin: declaredMin };
  }
  const effectiveLatest = storeVersion;
  const effectiveMin =
    compareVersions(storeVersion, declaredMin) < 0
      ? storeVersion
      : declaredMin;
  return { effectiveLatest, effectiveMin };
};

/**
 * Diagnostic log for the update-gate, deduped per app session: re-emits only
 * when the outcome actually changes (status / current / store version), so an
 * outdated app foregrounding repeatedly produces one event per state, not per
 * open. Sinks to app_event_logs + PostHog via logInfo (category "feature").
 */
let lastVersionCheckSignature: string | null = null;
const maybeLogVersionCheck = (details: {
  platform: AppPlatform;
  currentVersion: string;
  storeVersion: string | null;
  latestVersion: string;
  minSupportedVersion: string;
  bannerEnabled: boolean;
  blockingEnabled: boolean;
  storeLookupFailed: boolean;
  status: UpdateStatus;
}): void => {
  const signature = `${details.status}:${details.currentVersion}:${details.storeVersion ?? "null"}`;
  if (signature === lastVersionCheckSignature) return;
  lastVersionCheckSignature = signature;
  logInfo("feature", "app_update_check", details);
};

/**
 * Performs a single version check against the app_versions table for the
 * current native platform. On web (or any error) this resolves to
 * { status: 'ok' } so it never blocks the app.
 */
export const checkAppVersion = async (): Promise<AppVersionCheckResult> => {
  const platform = getPlatform();
  if (!platform) return DEFAULT_RESULT;

  const currentVersion = await getCurrentVersion();
  if (!currentVersion) return DEFAULT_RESULT;

  let row: AppVersionRow | null = null;
  try {
    const { data, error } = await supabase
      .from("app_versions")
      .select("*")
      .eq("platform", platform)
      .maybeSingle();
    if (error) {
      // Table missing, RLS issue, network — fail open.
      console.warn("[appVersion] check skipped:", error.message);
      return { ...DEFAULT_RESULT, currentVersion };
    }
    row = (data as AppVersionRow | null) ?? null;
  } catch (e) {
    console.warn("[appVersion] check threw:", e);
    return { ...DEFAULT_RESULT, currentVersion };
  }

  if (!row) return { ...DEFAULT_RESULT, currentVersion };

  const cmpDeclaredLatest = compareVersions(
    currentVersion,
    row.latest_version,
  );
  const cmpDeclaredMin = compareVersions(
    currentVersion,
    row.min_supported_version,
  );

  // Self-report: if this device is running a version newer than what the
  // database knows about, bump latest_version so the table stays in sync
  // (the banner itself is store-driven, see below).
  if (cmpDeclaredLatest > 0) {
    void reportVersion(platform, currentVersion);
  }

  // Only hit the store when the DB says this device might be behind.
  // latest_version normally leads the store — the team's own pre-release
  // build reports it — so "current >= latest_version" means up-to-date and we
  // can skip the network call for those users. (If no admin ran the build
  // first, it trails the store until three members have updated, and outdated
  // devices go unprompted for that long.) Devices that ARE behind hit
  // the (short-TTL) store lookup and the banner is then driven by what the
  // store actually serves — so it shows on essentially every open of an
  // outdated app, not just a window right after release.
  const needsStoreCheck = cmpDeclaredLatest < 0 || cmpDeclaredMin < 0;
  const storeVersion = needsStoreCheck
    ? await getCachedStoreVersion(platform, row)
    : null;

  const { effectiveLatest, effectiveMin } = computeEffectiveVersions(
    row.latest_version,
    row.min_supported_version,
    storeVersion,
  );
  const cmpMin = compareVersions(currentVersion, effectiveMin);
  const cmpLatest = compareVersions(currentVersion, effectiveLatest);

  let status: UpdateStatus = "ok";
  if (row.blocking_enabled && cmpMin < 0) {
    status = "force_update";
  } else if (row.banner_enabled && cmpLatest < 0) {
    status = "soft_update";
  }

  // Instrumentation: only for devices the DB flags as behind (the population
  // that should be seeing a banner). Lets us verify on the next release
  // whether outdated apps actually prompt, and catches the silent failure
  // where we're behind but the store lookup returned null (storeVersion).
  // Deduped per session so foreground churn doesn't spam the pipeline.
  if (needsStoreCheck) {
    maybeLogVersionCheck({
      platform,
      currentVersion,
      storeVersion,
      latestVersion: row.latest_version,
      minSupportedVersion: row.min_supported_version,
      bannerEnabled: row.banner_enabled,
      blockingEnabled: row.blocking_enabled,
      storeLookupFailed: storeVersion === null,
      status,
    });
  }

  return {
    status,
    currentVersion,
    latestVersion: effectiveLatest,
    releaseNotes: row.release_notes,
    storeUrl: row.store_url,
  };
};
